/**
 * Butterfly stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const butterflyStickersPack: ToilePack = {
    slug: 'butterfly-stickers',
    category: 'sticker',
    name: 'Butterfly stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'monarch', label: 'Monarch', subject: 'a monarch butterfly with orange and black wings open flat'},
        {key: 'morpho', label: 'Blue morpho', subject: 'a blue morpho butterfly with iridescent wings open flat'},
        {key: 'swallowtail', label: 'Swallowtail', subject: 'a yellow swallowtail butterfly with tails on its wings open flat'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'vinyl', label: 'Vinyl', style: 'glossy vinyl sticker, clean vector shapes, bold flat colour, a small highlight', palette: 'Bright flat colour with a white highlight', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
    ],
    directions: ['a moth instead', 'for a journal cover', 'as a matching pair'],
    directionHint: 'try: a moth instead',
    seo: {
        title: 'Butterfly Stickers: Monarch, Morpho, Swallowtail',
        metaDescription: 'Nine butterfly stickers — monarch, blue morpho, swallowtail, wings open flat — in holographic, kawaii, and vinyl styles.',
        h1: 'Butterfly stickers: monarch, blue morpho, swallowtail',
        tagline: 'Nine stickers, three butterflies, three styles each.',
        intro: 'Three butterflies with wings open flat: a monarch in orange and black, a blue morpho with iridescent wings, and a yellow swallowtail with tailed wingtips. Each rendered holographic, kawaii, and glossy vinyl. Ask for a moth instead, a garden scene, or a different colour combination, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a planner or journal page',
            'a phone case sticker sheet',
            "a nature-lover's gift",
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine butterfly stickers — a monarch, a blue morpho, and a swallowtail, wings open flat — in holographic, kawaii, and glossy vinyl finishes.'},
            {q: 'Can I get a moth or a different butterfly?', a: 'Yes — the direction field takes any variation, including a moth instead, a garden scene, or a different colour combination.'},
            {q: 'What does the holographic finish print as?', a: 'The holographic design is an illustrated iridescent sheen — print it on holographic vinyl stock for the real shifting effect, or on plain matte stock for a flat colourful version.'},
            {q: 'Are these accurate species illustrations?', a: "They're recognisable takes on real species — a monarch, a blue morpho, a swallowtail — styled for stickers, not a field-guide reference."},
        ],
    },
}
